import { DownOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  DatePickerProps,
  Form,
  FormInstance,
  Input,
  InputNumber,
  message,
  Row,
  Tooltip,
  Upload,
} from 'antd';
import { RcFile, UploadProps } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { isDefined } from 'class-validator';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import { customMarkers } from 'src/app/components/LeafletMapComponent/leaflet.config';
import LeafletMap from 'src/app/components/LeafletMapComponent/LeafletMap';
import OptionTags from 'src/app/components/OptionTagsComponent/OptionTags';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import TooltipWithIcon from 'src/app/components/TooltipWithIconComponent/TooltipWithIcon';
import 'src/app/forms/FileUploadForm/file-upload-form.scss';
import styles from 'src/app/forms/FileUploadForm/FileUploadForm.module.scss';
import { IFormProps } from 'src/app/forms/form.config';
import InteractionForm from 'src/app/forms/InteractionForm/InteractionForm';
import LocationIdentifiedForm from 'src/app/forms/LocationIdentifiedForm/LocationIdentifiedForm';
import PersonIdentifiedForm from 'src/app/forms/PersonIdentifiedForm/PersonIdentifiedForm';
import TopicIdentifiedForm from 'src/app/forms/TopicIdentifiedForm/TopicIdentifiedForm';
import VehicleIdentifiedForm from 'src/app/forms/VehicleIdentifiedForm/VehicleIdentifiedForm';
import { getEnumKeyByValue, getEnumSelectOptions } from 'src/app/helpers/enum.helper';
import { convertInterestListToSelectOptions } from 'src/app/helpers/service.helper';
import { FileUploadDataType } from 'src/app/redux/fileUpload/fileUpload-slice.type';
import { useUploadFileMutation } from 'src/app/redux/fileUpload/fileUploadSlice';
import { useAppDispatch, useAppSelector } from 'src/app/redux/hook';
import { useGetAllInterestsQuery } from 'src/app/redux/interests/interestsSlice';
import { useGetLocationsIdentifiedAllQuery } from 'src/app/redux/locations/locationsIdentifiedSlice';
import { MapInstance } from 'src/app/redux/map/map.redux.type';
import { setMapInstance } from 'src/app/redux/map/mapSlice';
import { useGetSensorsIdentifiedQuery } from 'src/app/redux/sensors/sensorIdentifiedSlice';
import systemConfig, { INTERESTS_WHITELIST } from 'src/configs/system.config';
import { InterestClassType, InterestsTypes } from 'src/enums/interests.enum';
import { DeleteIcon } from 'src/icons';
import AddIcon from 'src/icons/AddIcon';
import FileUploadIcon from 'src/icons/FileUploadIcon';
import LocationIcon from 'src/icons/LocationIcon';

interface FormOptions {
  value: string;
  label: string;
}

const ADDABLE_INTEREST_LIST: InterestsTypes[] = [
  InterestsTypes.Person,
  InterestsTypes.Vehicle,
  InterestsTypes.Topic,
  InterestsTypes.Location,
  // InterestsTypes.Relation,
];

const EXCLUDE_INTEREST_LIST: InterestsTypes[] = [InterestsTypes.Sensor, InterestsTypes.Relation];

const AddInterestForm = ({
  selectedInterestType,
  form,
  destroyModal,
}: {
  selectedInterestType: InterestsTypes;
  form: FormInstance;
  destroyModal: () => void;
}) => {
  if (selectedInterestType === InterestsTypes.Person)
    return <PersonIdentifiedForm form={form} destroyModal={destroyModal} />;

  if (selectedInterestType === InterestsTypes.Vehicle)
    return <VehicleIdentifiedForm form={form} destroyModal={destroyModal} />;

  if (selectedInterestType === InterestsTypes.Topic)
    return <TopicIdentifiedForm form={form} destroyModal={destroyModal} />;

  if (selectedInterestType === InterestsTypes.Relation)
    return <InteractionForm form={form} destroyModal={destroyModal} />;

  if (selectedInterestType === InterestsTypes.Location)
    return <LocationIdentifiedForm form={form} destroyModal={destroyModal} />;

  return null;
};

const FileUploadForm: React.FC<IFormProps> = ({ form }) => {
  const dispatch = useAppDispatch();
  const [addInterestForm] = Form.useForm();
  const [locationOptions, setLocationOptions] = useState<FormOptions[]>([]);
  const [sensorOptions, setSensorOptions] = useState<FormOptions[]>([]);
  // const [functionalAreaOptions, setFunctionalAreasOptions] = useState<FormOptions[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [time, setTime] = useState<string>('');
  const [fileList, setFileList] = useState<File[]>([]);
  const { theme } = useAppSelector((state) => state.configContent);
  const location = Form.useWatch('locations', form);
  const lat = Form.useWatch('lat', form);
  const lng = Form.useWatch('lng', form);
  const uploadedFiles = Form.useWatch('file', form);
  const locations = useGetLocationsIdentifiedAllQuery({ isInterested: true });
  const sensors = useGetSensorsIdentifiedQuery();
  // const functionalAreas = useGetFunctionalAreasRecognizedQuery();
  const [uploadFile] = useUploadFileMutation();

  const selectedInterestType = Form.useWatch('interestType', { form });
  const selectedInterestNames = Form.useWatch('interestName', form);
  const selectedLatitude = Form.useWatch('lat', form);
  const selectedLongitude = Form.useWatch('lng', form);
  const selectedSensors = Form.useWatch('sensors', form);

  const [groupedInterests, setGroupedInterests] = useState<any>([]);

  // useEffect(() => {
  //   if (!selectedInterestNames) return;
  //   setGroupedInterests((prev: any) => [
  //     ...prev,
  //     ...selectedInterestNames.map((opt: any) => ({ interestId: opt.value, type: selectedInterestType })),
  //   ]);
  // }, [selectedInterestNames]);

  const { data: interestList } = useGetAllInterestsQuery(
    {
      types: [selectedInterestType!],

      order: 'name',
      customerStatus: selectedInterestType === InterestsTypes.Enterprise ? true : undefined,
      interestsClassTypes:
        selectedInterestType === InterestsTypes.Enterprise ? InterestClassType.Identified : undefined,
      isProxy: selectedInterestType === InterestsTypes.Enterprise ? false : undefined,
    },
    {
      skip: !isDefined(selectedInterestType),
    },
  );

  const readFileAsBase64 = (file: File): Promise<string> =>
    new Promise((resolve, _reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const b64 = reader.result as string;
        resolve(b64);
      };
      reader.readAsDataURL(file as Blob);
    });

  const onDateChange: DatePickerProps['onChange'] = (date, dateString) => {
    let formattedDate;
    if (typeof dateString === 'string') {
      formattedDate = dateString.split(' ');
      const secondsFromFormattedDate = formattedDate.pop();
      const newSeconds = `${secondsFromFormattedDate}:00`;
      formattedDate.push(newSeconds);
      formattedDate = formattedDate.join('T');
    }
    if (formattedDate !== undefined) {
      setTime(formattedDate);
    }
  };

  const onFinish = async (values: any) => {
    const files: FileUploadDataType = [];

    for (let i = 0; i < fileList.length; i += 1) {
      const file = fileList[i];

      const base64File = await readFileAsBase64(file);
      files.push({
        data: base64File.split(',')[1],
        filename: file.name,
        type: file.type,
      });
    }
    const finalValues = {
      ...(values.lat && { latitude: values.lat }),
      ...(values.lng && { longitude: values.lng }),
      ...(values.locations && { locationIdentifyClassId: values.locations }),
      ...(values.sensors && { sensorIdentifyClassId: values.sensors }),
      ...(values.time && { uploadTime: time }),
      ...(values.title && { title: values.title }),
      ...(values.interestName && {
        interests: groupedInterests,
      }),
      // ...(values.functionalAreas && values?.functionalAreas?.length > 0 && { functionalAreas: values.functionalAreas }),
    };
    uploadFile({ fileList: files, ...finalValues });
    setFileList([]);
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const getLocationsList = async () => {
    if (locations.data) {
      const options = locations.data.rows.map((row) => ({
        value: row.id,
        label: `${row.name}`,
      }));

      setLocationOptions(options);
    }
  };

  const getSensorsList = async () => {
    if (sensors.data) {
      const options = sensors.data.rows.map((row) => ({
        value: row.id,
        label: `${row.name}`,
      }));
      setSensorOptions(options);
    }
  };

  // const getFunctionalAreasList = async () => {
  //   if (functionalAreas.data) {
  //     const options = functionalAreas.data.rows.map((row) => ({
  //       value: row.id,
  //       label: `${row.name}`,
  //     }));
  //     setFunctionalAreasOptions(options);
  //   }
  // };

  // discrad files function
  const discardFiles = async () => {
    setFileList([]);
    message.info(t('forms.fileUploadForm.uploadedField.message'));
  };
  const generateSuccessfullyUploadedMessage = () => {
    if (isDefined(fileList) && fileList.length > 0) {
      return (
        <div className={styles.uploaded}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '20px' }}>{t('forms.fileUploadForm.uploadedField.header')}:</span>
            {fileList.length > 0 && (
              <Tooltip
                placement="top"
                title={t('forms.fileUploadForm.uploadedField.tooltip', { listLength: fileList.length })}
              >
                <Button
                  style={{ backgroundColor: theme.colors.quoteBgc, maxWidth: 'unset' }}
                  className={styles.discardFilesBtn}
                  onClick={() => discardFiles()}
                >
                  <span style={{ padding: '0px 10px' }}>{t('forms.fileUploadForm.uploadedField.buttonText')}</span>
                  {/* <DeleteIcon color="#ccc" /> */}
                </Button>
              </Tooltip>
            )}
          </div>

          <ul className={styles.uploadedList} style={{ padding: '10px 0', margin: 0 }}>
            {fileList.map((file, index) => (
              <li key={index} className={styles.file}>
                <span style={{ display: 'flex', alignItems: 'center' }}> &#8226; {file.name}</span>
                <div
                  onClick={() => {
                    setFileList((prev: File[]) => prev.filter((f: File) => f.name !== file.name));
                  }}
                >
                  <TooltipWithIcon
                    placement="left"
                    title={t('forms.fileUploadForm.uploadedField.buttonTextTooltip', { fileName: file.name })}
                  >
                    <DeleteIcon color="rgba(194, 33, 33, 0.4)" style={{ cursor: 'pointer' }} />
                  </TooltipWithIcon>
                </div>
              </li>
            ))}
          </ul>
        </div>
      );
    }
    return '';
  };

  useEffect(() => {
    getLocationsList();
    getSensorsList();
    // getFunctionalAreasList();
  }, [locations, sensors]);

  useEffect(() => {
    const locationIdentified = locations.data?.rows.find((row) => row.id === location);
    if (locationIdentified) {
      const locationLat = locationIdentified?.lat;
      const locationLng = locationIdentified?.lng;

      form.setFieldValue('lat', locationLat);
      form.setFieldValue('lng', locationLng);
    }
  }, [location]);

  useEffect(() => {
    const locationIdentified = locations.data?.rows.find((row) => row.id === location);
    const locationLat = locationIdentified?.lat;
    const locationLng = locationIdentified?.lng;
    if (locationIdentified && (locationLat !== lat || locationLng !== lng)) {
      form.resetFields(['locations']);
    }
  }, [lat, lng]);

  const { AllowImage, AllowVideo } = systemConfig.fileUploadTypes;

  const isAllowed = (file: RcFile): boolean => {
    if (!AllowImage && file.type.startsWith('image/')) {
      return false;
    }
    if (!AllowVideo && file.type.startsWith('video/')) {
      return false;
    }
    return true;
  };

  const beforeUpload = (_file: any, files: RcFile[]) => {
    // setFileList([]);
    const maxSize = 100 * 1024 * 1024; // 100 MB

    const fileName = files.map((file: any) => file.name)[0];
    const errMessage = t('forms.fileUploadForm.info.errMessage', {
      fileName,
    });

    let total = 0;
    const invalidFiles = files.filter((file) => {
      total += file.size;
      return !isAllowed(file);
    });

    if (total > maxSize) {
      if (errorMessage !== errMessage) {
        setErrorMessage(errMessage);
      }
      return Upload.LIST_IGNORE;
    }

    if (invalidFiles.length > 0) {
      // const invalidFileNames = invalidFiles.map((file) => file.name).join(', ');
      const notAllowed = (!AllowImage && 'image') || (!AllowVideo && 'video') || 'image and video';
      const invalidTypeError = t('forms.fileUploadForm.info.invalidTypeError', {
        notAllowed,
      });
      setErrorMessage(invalidTypeError);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  useEffect(() => {
    if (!errorMessage) return;
    message.error(errorMessage);
    setErrorMessage('');
  }, [errorMessage]);

  const onFileUpload: UploadProps['onChange'] = (info: any) => {
    // gets the list of selected files and remove duplicates
    const existingFiles = fileList.filter((file) => file.name === info.file.name);
    if (existingFiles.length > 0) {
      setFileList(fileList.filter((file) => file.name !== info.file.name));
    }
    setFileList((prev) => [...prev, info.file]);
  };
  const onAddInterest = (event: any) => {
    event.stopPropagation();

    const modal = showModal({
      title: `Add ${getEnumKeyByValue(InterestsTypes, selectedInterestType)}`,
      content: (
        <AddInterestForm
          selectedInterestType={selectedInterestType}
          form={addInterestForm}
          destroyModal={() => modal.destroy()}
        />
      ),
      size: 'medium-2',
      onSave: () => addInterestForm.submit(),
    });
  };

  let markedLatLng: [number, number];

  const onSelectLatLng = (e: any) => {
    e.stopPropagation();
    dispatch(setMapInstance(MapInstance.MODAL));

    showModal({
      title: t('forms.fileUploadForm.fields.selectLatitude'),
      content: (
        <LeafletMap
          mapOptions={{
            center: [38.423733, 27.142826],
            zoom: 1,
            zoomSnap: 0.25,
            minZoom: 2,
            maxZoom: 15,
            maxBounds: [
              [-85, -180],
              [85, 180],
            ],
            maxBoundsViscosity: 1.0,
            attributionControl: false,
          }}
          drawOptions={{
            draw: {
              circle: false,
              circlemarker: false,
              polygon: false,
              polyline: false,
              rectangle: false,
              marker: {
                icon: customMarkers.defaultMarker('custom'),
              },
            },
          }}
          mapId={MapInstance.MODAL}
          style={{ height: '70vh' }}
          events={{
            onDrawCompleted(event) {
              const { layer, layerType } = event;

              if (layerType === 'marker') {
                // @ts-ignore
                markedLatLng = [layer._latlng.lat, layer._latlng.lng];
              }
            },
          }}
          lastCoordinates={
            selectedLatitude && selectedLongitude
              ? [{ latitude: form.getFieldValue('lat'), longitude: form.getFieldValue('lng') }]
              : []
          }
        />
      ),
      size: 'xlarge-2',
      disableCancelButton: true,
      okText: 'Mark',
      onSave: (modal) => {
        if (!markedLatLng) {
          message.error('Please select coordinates first.');
          return;
        }
        const [selectedLat, selectedLng] = markedLatLng;
        form.setFieldValue('lat', selectedLat);
        form.setFieldValue('lng', selectedLng);
        dispatch(setMapInstance(MapInstance.DEFAULT));

        modal.destroy();
      },
      onCancel() {
        dispatch(setMapInstance(MapInstance.DEFAULT));
      },
    });
  };

  return (
    <div id="file-upload-form">
      <FormComponent options={{ form, onFinish }}>
        <Row>
          <Col span={12} style={{ display: 'flex' }}>
            <Form.Item
              name="file"
              getValueFromEvent={normFile}
              style={{ marginBottom: '12px', justifyContent: 'right !important', display: 'block' }}
              className="borderless custom-error"
              rules={[{ required: true }]}
            >
              <Dragger
                openFileDialogOnClick={false}
                className={styles.fileDragger}
                style={{ display: 'block', width: '800px', height: '175px' }}
                beforeUpload={beforeUpload}
                onChange={onFileUpload}
                multiple
                directory
                itemRender={(_node, _file, _fileList, _actions) => undefined}
              >
                <div className={styles.fileUploadContainer}>
                  <FileUploadIcon style={{ height: '60px', width: '60px' }} color="white" />
                  <span style={{ color: 'white', paddingBlock: '12px' }}>
                    <b>{t('forms.fileUploadForm.info.dragFileHere')}</b>
                  </span>
                  {/* <div className={styles.uploadButtonsContainer}> */}
                  <Row style={{ width: '100%', alignContent: 'space-evenly' }}>
                    <Col span={12}>
                      <Form.Item
                        name="file"
                        getValueFromEvent={normFile}
                        style={{ marginBottom: '12px', justifyContent: 'right !important' }}
                        className="borderless"
                      >
                        <Upload
                          multiple
                          onChange={onFileUpload}
                          beforeUpload={beforeUpload}
                          style={{ padding: 0 }}
                          itemRender={(_node, _file, _fileList, _actions) => undefined}
                        >
                          <Button style={{ backgroundColor: theme.colors.quoteBgc, maxWidth: 'unset' }}>
                            {t('forms.fileUploadForm.fields.selectFile')}
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="file" getValueFromEvent={normFile} className="borderless">
                        <Upload
                          multiple
                          directory
                          beforeUpload={beforeUpload}
                          onChange={onFileUpload}
                          itemRender={(_node, _file, _fileList, _actions) => undefined}
                        >
                          <Button style={{ backgroundColor: theme.colors.quoteBgc }} className={styles.fileUploadBtn}>
                            {t('forms.fileUploadForm.fields.selectFolder')}
                          </Button>
                        </Upload>
                      </Form.Item>
                    </Col>
                  </Row>
                  {/* </div> */}
                </div>
              </Dragger>
            </Form.Item>
            {generateSuccessfullyUploadedMessage()}
          </Col>
          <Col span={12}>
            <Form.Item label={t('forms.fileUploadForm.fields.locations')} name="locations">
              <SearchableSelect variant="borderless" options={locationOptions} allowClear showSearch />
            </Form.Item>
            <Form.Item label={t('forms.fileUploadForm.fields.title')} name="title">
              <Input variant="borderless" />
            </Form.Item>
            <Form.Item label={t('forms.fileUploadForm.fields.time')} htmlFor="time" name="time">
              <DatePicker
                style={{ width: '100%' }}
                showTime
                variant="borderless"
                id="time"
                format="YYYY-MM-DD HH:mm"
                onChange={onDateChange}
              />
            </Form.Item>

            <Form.Item wrapperCol={{ span: 24 }}>
              <div style={{ flex: '1' }}>
                <Form.Item
                  style={{ borderTop: 'none' }}
                  labelCol={{ span: 9 }}
                  label={t('forms.fileUploadForm.fields.latitude')}
                  name="lat"
                >
                  <InputNumber style={{ width: '100%' }} variant="borderless" />
                </Form.Item>
                <Form.Item
                  style={{ borderBottom: 'none' }}
                  labelCol={{ span: 9 }}
                  label={t('forms.fileUploadForm.fields.longtitude')}
                  name="lng"
                >
                  <InputNumber style={{ width: '100%' }} variant="borderless" />
                </Form.Item>
              </div>
              <Form.Item
                className="map-icon-form-item"
                style={{
                  borderInlineStart: `1px solid ${theme.colors.quoteBgc}`,
                  borderBottom: 'none',
                  display: 'flex',
                }}
                label={
                  <Tooltip title={t('forms.fileUploadForm.info.showInMap')}>
                    <div>
                      <LocationIcon
                        color="white"
                        circleColor={theme.colors.quoteBgc}
                        style={{ pointerEvents: 'auto', margin: 0, height: '25px' }}
                        onClick={(e) => onSelectLatLng(e)}
                      />
                    </div>
                  </Tooltip>
                }
              />
            </Form.Item>

            <Form.Item label={t('forms.fileUploadForm.fields.sensors')} name="sensors">
              <SearchableSelect variant="borderless" options={sensorOptions} allowClear showSearch />
            </Form.Item>
            <Form.Item
              label={t('forms.fileUploadForm.fields.interestType')}
              name="interestType"
              rules={[{ required: selectedSensors === '3d344ef4-2e5d-4c18-93b4-40f930e77c0c' }]}
            >
              <SearchableSelect
                variant="borderless"
                options={getEnumSelectOptions(InterestsTypes, INTERESTS_WHITELIST()).filter(
                  ({ value }) => !EXCLUDE_INTEREST_LIST.includes(Number(value)),
                )}
                onChange={(value) => form.setFieldValue('interestType', value)}
                suffixIcon={
                  <div style={{ display: 'flex', gap: '.5rem' }}>
                    {ADDABLE_INTEREST_LIST.includes(selectedInterestType) ? (
                      <Tooltip title={`Add ${getEnumKeyByValue(InterestsTypes, selectedInterestType)}`}>
                        <Button
                          onClick={(e) => onAddInterest(e)}
                          style={{
                            pointerEvents: 'auto',
                            cursor: 'pointer',
                            padding: 0,
                            background: theme.colors.quoteBgc,
                            border: 'none',
                            borderRadius: '50%',
                            height: '20px',
                          }}
                        >
                          <AddIcon style={{ height: '20px', width: '20px' }} color="white" />
                        </Button>
                      </Tooltip>
                    ) : null}
                    <DownOutlined />
                  </div>
                }
              />
            </Form.Item>
            <Form.Item
              label={t('forms.fileUploadForm.fields.interestName')}
              name="interestName"
              rules={[{ required: selectedSensors === '3d344ef4-2e5d-4c18-93b4-40f930e77c0c' }]}
            >
              <SearchableSelect
                labelInValue
                mode="multiple"
                options={convertInterestListToSelectOptions(interestList?.rows ?? [])}
                variant="borderless"
                // eslint-disable-next-line react/jsx-no-useless-fragment
                tagRender={() => <></>}
                onChange={(_, opt) => {
                  let foundClassType: InterestClassType;
                  interestList?.rows.forEach((row) => {
                    row.interests.forEach((int) => {
                      if (int.id === opt.at(-1).value) {
                        foundClassType = int.classType;
                      }
                    });
                  });
                  setGroupedInterests((prev: any) => [
                    ...prev,
                    { interestId: opt.at(-1).value, type: selectedInterestType, classType: foundClassType },
                  ]);
                }}
              />
            </Form.Item>
            {selectedInterestNames?.length ? (
              <OptionTags
                onDelete={(filtered) => form.setFieldValue('interestName', filtered)}
                options={selectedInterestNames}
              />
            ) : null}
            {/* <Form.Item label={t('forms.fileUploadForm.fields.functionalAreas')} name="functionalAreas">
            <SearchableSelect mode="multiple" variant="borderless" options={functionalAreaOptions} />
            </Form.Item> */}
          </Col>
        </Row>
      </FormComponent>
    </div>
  );
};

export default FileUploadForm;
